/**
 * One register: the header everything shares, and the tab strip.
 *
 * Overview is the landing tab, because it is the authoritative record everything else derives
 * from. The matter tabs are their own routes and their own read.
 */

import { Link, Outlet, useParams, useLocation } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { AppShell } from '~/layout/AppShell';
import { QueryError } from '~/layout/ErrorBoundary';
import { Tabs, TabPanel } from '~/ui/Tabs';
import { SkeletonStack } from '~/ui/Surface';
import { EmptyState } from '~/ui/Feedback';
import { useJurisdiction, useMeta } from '~/api/queries';
import { routes, slugForMatter } from '~/lib/routes';
import { fmtCount } from '~/lib/format';
import { CodeChip, LineageLines, lineageFacts } from './RegisterName';
import { StatusPill, ResearchStatusBadge } from './pills';
import { ManageMappingBadge } from './ManageMappingBadge';
import { Pill } from '~/ui/Pill';
import styles from './Overview.module.css';

export const TAB_PANEL_ID = 'register-panel';

export function JurisdictionLayout() {
  const { code = '' } = useParams();
  const { pathname } = useLocation();
  const register = useJurisdiction(code);
  const meta = useMeta();

  if (register.isError) {
    return (
      <AppShell trail={[{ label: 'Legal jurisdictions', href: routes.jurisdictions() }]} leaf={code}>
        <QueryError error={register.error} onRetry={() => void register.refetch()} />
      </AppShell>
    );
  }

  if (register.isPending || meta.isPending) {
    return (
      <AppShell trail={[{ label: 'Legal jurisdictions', href: routes.jurisdictions() }]} leaf={code} width="reading">
        <SkeletonStack rows={6} height={48} />
      </AppShell>
    );
  }

  const data = register.data;
  const matters = meta.data?.matters ?? [];

  const activeMatterSlug = pathname.split('/')[3] ?? 'overview';
  const selected =
    activeMatterSlug === 'overview'
      ? 'overview'
      : (matters.find((matter) => slugForMatter(matter.code) === activeMatterSlug)?.code ??
        'overview');

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      href: routes.overview(data.code),
      count: data.lifecycle.length,
    },
    ...matters.map((matter) => {
      const summary = data.matters.find((entry) => entry.matter === matter.code);
      return {
        key: matter.code,
        label: matter.label,
        href: routes.timeline(data.code, matter.code),
        count: summary ? summary.versions : '—',
      };
    }),
  ];

  const facts = lineageFacts(data);

  return (
    <AppShell
      trail={[{ label: 'Legal jurisdictions', href: routes.jurisdictions() }]}
      leaf={data.name}
      width="reading"
    >
      <Link to={routes.jurisdictions()} className={styles.backlink}>
        <ArrowLeft aria-hidden="true" size={14} /> All jurisdictions
      </Link>

      <div className={styles.detailHead}>
        <div>
          <h1>
            <CodeChip code={data.code} incomplete={data.incomplete} />
            {data.name}
            <StatusPill status={data.status} />
            {data.incomplete ? <Pill tone="quest" size="mini">Incomplete</Pill> : null}
            <ResearchStatusBadge status={data.researchStatus} />
            <ManageMappingBadge matters={data.matters} />
          </h1>
          <div className={styles.detailSub}>
            {data.registry} · Register opened {fmtCount(data.founded)}
            {data.openedPrecision === 'year' ? ' (year only)' : ''}
            <div className={styles.timezone}>
              {data.timezone}
              {data.timezoneIsDefault ? ' (default)' : ''} · today there is {data.today}
            </div>
            <LineageLines facts={facts} />
          </div>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        value={selected}
        aria-label="This register"
        panelId={TAB_PANEL_ID}
        idPrefix="register-tab"
      />
      <TabPanel id={TAB_PANEL_ID} labelledBy={`register-tab-${selected}`}>
        {matters.length === 0 && selected === 'overview' ? (
          <EmptyState>
            <p>
              The engine holds no matter vocabulary, so this register&rsquo;s tabs cannot be
              built.
            </p>
          </EmptyState>
        ) : (
          <Outlet />
        )}
      </TabPanel>
    </AppShell>
  );
}
